import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  Paper,
  Tooltip,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PurchaseOrderSummary from '../../components/purchasing/PurchaseOrderSummary';
import PurchaseLine from '../../components/purchasing/PurchaseLine';
import SupplierPicker from '../../components/purchasing/SupplierPicker';
import SeasonPicker from '../../components/purchasing/SeasonPicker';
import {
  canForceTransferWithMissingSize,
  FORCE_TRANSFER_MISSING_SIZE_CODE,
  formatMissingFields,
} from '../../components/purchasing/ReadinessBadge';
import {
  AttributeOption,
  createEmptyLine,
  PurchaseLine as PurchaseLineType,
  PurchaseOrder,
  PurchaseOrderListItem,
} from '../../types/purchasing';
import {
  createPurchaseOrder,
  downloadPurchaseOrderNotePdf,
  fetchAttributes,
  fetchPurchaseOrderById,
  fetchPurchaseOrders,
  fetchSupplierBrands,
  fetchSuppliers,
  submitPurchaseOrder,
  transferPurchaseOrder,
  updatePurchaseOrder,
} from '../../services/purchasingService';
import { usePrompt } from '../../hooks/usePrompt';

interface PurchaseOrderEditProps {
  isReadOnly?: boolean;
}

const CURRENCIES = ['SEK', 'EUR', 'USD', 'GBP'];

const MONTH_FMT = new Intl.DateTimeFormat('sv-SE', {
  month: 'long',
  year: 'numeric',
});

function monthEntry(
  year: number,
  month: number,
): { value: string; label: string } {
  const d = new Date(year, month, 1);
  return {
    value: `${year}-${String(month + 1).padStart(2, '0')}`,
    label: MONTH_FMT.format(d).replace(/^./, (c) => c.toUpperCase()),
  };
}

/** Generates the relevant delivery-month window based on the selected season.
 *  AW{YY} → April 20YY – February 20(YY+1)
 *  SS{YY} → October 20(YY-1) – June 20YY
 *  No / unrecognised season → rolling 24 months from today.
 */
function generateDeliveryMonths(
  season?: string | null,
  pinValue?: string | null,
): { value: string; label: string }[] {
  const match = season?.match(/^(SS|AW)(\d{2})$/i);

  let months: { value: string; label: string }[];

  if (match) {
    const type = match[1].toUpperCase();
    const year = 2000 + parseInt(match[2], 10);

    let startYear: number,
      startMonth: number,
      endYear: number,
      endMonth: number;
    if (type === 'AW') {
      startYear = year;
      startMonth = 3; // April
      endYear = year + 1;
      endMonth = 1; // February
    } else {
      startYear = year - 1;
      startMonth = 9; // October
      endYear = year;
      endMonth = 5; // June
    }

    months = [];
    let d = new Date(startYear, startMonth, 1);
    const endD = new Date(endYear, endMonth, 1);
    while (d <= endD) {
      months.push(monthEntry(d.getFullYear(), d.getMonth()));
      d = new Date(d.getFullYear(), d.getMonth() + 1, 1);
    }
  } else {
    const now = new Date();
    months = Array.from({ length: 24 }, (_, i) =>
      monthEntry(now.getFullYear(), now.getMonth() + i),
    );
  }

  // Always keep the currently pinned value in the list so the field never shows blank.
  if (pinValue && !months.some((m) => m.value === pinValue)) {
    const [y, mo] = pinValue.split('-').map(Number);
    months = [monthEntry(y, mo - 1), ...months];
  }

  return months;
}

const initialOrder: PurchaseOrder = {
  orderNumber: '',
  supplierId: 0,
  supplierOrderNumber: '',
  currency: 'SEK',
  season: '',
  defaultDeliveryMonth: `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
  paymentTermsDays: 0,
  status: 'draft',
  notes: '',
  lines: [
    createEmptyLine(
      0,
      `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
    ),
  ],
};

function orderFingerprint(order: PurchaseOrder): string {
  const normalized = {
    purchaseOrderId: (order as any).purchaseOrderId ?? null,
    orderNumber: order.orderNumber ?? '',
    supplierId: order.supplierId ?? 0,
    supplierOrderNumber: order.supplierOrderNumber ?? '',
    currency: order.currency ?? 'SEK',
    season: order.season ?? '',
    defaultDeliveryMonth: order.defaultDeliveryMonth ?? '',
    paymentTermsDays: order.paymentTermsDays ?? 0,
    status: order.status ?? 'draft',
    notes: order.notes ?? '',
    lines: (order.lines ?? []).map((line: any) => ({
      purchaseLineId: line.purchaseLineId ?? null,
      lineIndex: line.lineIndex ?? null,
      source: line.source ?? null,
      brandName: line.brandName ?? null,
      articleNumber: line.articleNumber ?? '',
      productName: line.productName ?? '',
      bulkSupplierPrice: line.bulkSupplierPrice ?? null,
      bulkPriceOrig: line.bulkPriceOrig ?? null,
      bulkPrice: line.bulkPrice ?? null,
      bulkPriceOrigDkk: line.bulkPriceOrigDkk ?? null,
      bulkPriceDkk: line.bulkPriceDkk ?? null,
      manualPriceColors: line.manualPriceColors ?? [],
      skus: (line.skus ?? []).map((sku: any) => ({
        purchaseLineSkuId: sku.purchaseLineSkuId ?? null,
        sourceSkuId: sku.sourceSkuId ?? null,
        pluNo: sku.pluNo ?? null,
        attribute1Code: sku.attribute1Code ?? null,
        attribute2Code: sku.attribute2Code ?? null,
        attribute3Code: sku.attribute3Code ?? null,
        supplierPrice: sku.supplierPrice ?? null,
        priceOrig: sku.priceOrig ?? null,
        price: sku.price ?? null,
        priceOrigDkk: sku.priceOrigDkk ?? null,
        priceDkk: sku.priceDkk ?? null,
        weight: sku.weight ?? null,
        deliveryMonth: sku.deliveryMonth ?? null,
        quantity: sku.quantity ?? 0,
      })),
    })),
  };

  return JSON.stringify(normalized);
}

const PurchaseOrderEdit: React.FC<PurchaseOrderEditProps> = ({
  isReadOnly = false,
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const purchaseOrderId = id ? Number(id) : null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [order, setOrder] = useState<PurchaseOrder>(initialOrder);
  const [savedFingerprint, setSavedFingerprint] = useState<string>(
    orderFingerprint(initialOrder),
  );
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [attributes, setAttributes] = useState<{
    attribute1: any[];
    attribute2: any[];
    attribute3: any[];
  }>({ attribute1: [], attribute2: [], attribute3: [] });
  const [autoSavedAt, setAutoSavedAt] = useState<Date | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [newDialogOpen, setNewDialogOpen] = useState<boolean>(!purchaseOrderId);
  const [newForm, setNewForm] = useState<{
    supplierId: number | null;
    season: string | null;
    paymentTermsDays: number;
    notes: string;
  }>({
    supplierId: null,
    season: null,
    paymentTermsDays: 0,
    notes: '',
  });
  const [dupConfirmOpen, setDupConfirmOpen] = useState(false);
  const [dupCandidate, setDupCandidate] =
    useState<PurchaseOrderListItem | null>(null);
  const [forceTransferConfirmOpen, setForceTransferConfirmOpen] =
    useState(false);

  const editable =
    !isReadOnly &&
    ['draft', 'submitted', 'transfer_failed'].includes(order.status);
  const isDirty = editable && orderFingerprint(order) !== savedFingerprint;
  const canForceMissingSizeTransfer = useMemo(
    () =>
      !order.readyForTransfer &&
      canForceTransferWithMissingSize(order.missingFieldsSummary),
    [order.readyForTransfer, order.missingFieldsSummary],
  );

  usePrompt(
    'Du har osparade ändringar. Vill du lämna sidan ändå?',
    isDirty && !saving,
  );

  const handleBack = () => {
    if (!purchaseOrderId) {
      navigate('/purchasing/purchase-orders');
      return;
    }
    if (isDirty && !saving) {
      const ok = window.confirm(
        'Du har osparade ändringar. Vill du lämna sidan ändå?',
      );
      if (!ok) return;
    }
    navigate('/purchasing/purchase-orders');
  };

  useEffect(() => {
    if (!isDirty || saving) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, saving]);

  const deliveryMonths = useMemo(
    () => generateDeliveryMonths(order.season, order.defaultDeliveryMonth),
    [order.season, order.defaultDeliveryMonth],
  );

  const loadOrder = async () => {
    setLoading(true);
    try {
      const [supplierData, attributeData] = await Promise.all([
        fetchSuppliers(),
        fetchAttributes(),
      ]);
      setSuppliers(supplierData);
      setAttributes(attributeData);

      if (!purchaseOrderId) {
        return;
      }

      const loadedOrder = await fetchPurchaseOrderById(purchaseOrderId);
      const nextOrder = {
        ...loadedOrder,
        lines: loadedOrder.lines.map((line) => ({ ...line, collapsed: false })),
      };
      setOrder(nextOrder);
      setSavedFingerprint(orderFingerprint(nextOrder));
    } catch (error) {
      console.error('Failed to load purchase order:', error);
      setErrorMessage('Kunde inte ladda inköpsordern.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrder();
  }, [id]);

  useEffect(() => {
    setNewDialogOpen(!purchaseOrderId);
  }, [purchaseOrderId]);

  const isNewFormValid = Boolean(newForm.supplierId && newForm.supplierId > 0);

  const tryCreateNewOrder = async () => {
    if (!isNewFormValid) return;
    const supplierId = newForm.supplierId!;
    const season = (newForm.season || '').trim() || null;

    try {
      const list = await fetchPurchaseOrders({ page: 1, limit: 200 });
      const candidate =
        list.purchaseOrders.find((o) => {
          const sameSupplier = o.supplierId === supplierId;
          const sameSeason = (o.season || null) === season;
          const isEmpty = (o.totalUnits || 0) === 0 && (o.totalCost || 0) === 0;
          return o.status === 'draft' && sameSupplier && sameSeason && isEmpty;
        }) ?? null;

      if (candidate) {
        setDupCandidate(candidate);
        setDupConfirmOpen(true);
        return;
      }
    } catch (error) {
      // Non-blocking: if list fetch fails we still allow creating an order.
      console.warn('Failed to check for duplicate empty orders:', error);
    }

    await createNewOrderNow();
  };

  const createNewOrderNow = async () => {
    if (!isNewFormValid) return;
    setSaving(true);
    setErrorMessage(null);
    try {
      const created = await createPurchaseOrder({
        supplierId: newForm.supplierId!,
        currency: 'SEK',
        season: (newForm.season || '').trim() || null,
        defaultDeliveryMonth: initialOrder.defaultDeliveryMonth,
        paymentTermsDays: newForm.paymentTermsDays ?? 0,
        notes: newForm.notes || '',
        supplierOrderNumber: '',
        lines: [createEmptyLine(0, initialOrder.defaultDeliveryMonth)],
      });
      const nextOrder = {
        ...created,
        lines: created.lines.map((line) => ({ ...line, collapsed: false })),
      };
      setOrder(nextOrder);
      setSavedFingerprint(orderFingerprint(nextOrder));
      setNewDialogOpen(false);
      navigate(`/purchasing/purchase-orders/edit/${created.purchaseOrderId}`, {
        replace: true,
      });
    } catch (error) {
      console.error('Failed to create purchase order:', error);
      setErrorMessage('Kunde inte skapa inköpsordern.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const loadSupplierData = async () => {
      if (!order.supplierId || order.supplierId <= 0) {
        setBrands([]);
        return;
      }

      try {
        const [brandData] = await Promise.all([
          fetchSupplierBrands(order.supplierId),
        ]);
        setBrands(brandData);
      } catch (error) {
        console.error('Failed to load supplier data:', error);
      }
    };
    loadSupplierData();
  }, [order.supplierId]);

  useEffect(() => {
    if (!editable || !order.purchaseOrderId) return;
    const timer = setInterval(async () => {
      const draftErrors = validateForDraft(order);
      if (draftErrors.length > 0) return;
      try {
        await updatePurchaseOrder(order.purchaseOrderId!, {
          supplierId: order.supplierId,
          currency: order.currency,
          season: order.season,
          defaultDeliveryMonth: order.defaultDeliveryMonth,
          paymentTermsDays: order.paymentTermsDays ?? 0,
          notes: order.notes,
          supplierOrderNumber: order.supplierOrderNumber,
          lines: order.lines.map((line) => ({
            ...line,
            lineIndex: line.lineIndex,
          })),
        });
        setAutoSavedAt(new Date());
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [order, editable]);

  const handleSupplierChange = (supplierId: number | null) => {
    const newId = supplierId || 0;
    if (newId === order.supplierId) return;

    const hasFilledLines = order.lines.some(
      (line) =>
        line.articleNumber?.trim() ||
        line.skus.some((sku) => (sku.quantity || 0) > 0),
    );

    if (hasFilledLines) {
      const confirmed = window.confirm(
        'Byt leverantör? Alla produktrader och valda varumärken kommer att rensas.',
      );
      if (!confirmed) return;
    }

    setOrder((current) => ({
      ...current,
      supplierId: newId,
      lines: [createEmptyLine(0, current.defaultDeliveryMonth)],
    }));
    setBrands([]);
  };

  const updateLine = (lineIndex: number, line: PurchaseLineType) => {
    setOrder((current) => ({
      ...current,
      lines: current.lines.map((row, index) =>
        index === lineIndex ? line : row,
      ),
    }));
  };

  const addLine = () => {
    setOrder((current) => ({
      ...current,
      lines: [
        ...current.lines.map((line) => ({ ...line, collapsed: true })),
        createEmptyLine(current.lines.length, current.defaultDeliveryMonth),
      ],
    }));
  };

  const removeLine = (lineIndex: number) => {
    setOrder((current) => {
      if (current.lines.length <= 1) {
        return {
          ...current,
          lines: [createEmptyLine(0, current.defaultDeliveryMonth)],
        };
      }
      return {
        ...current,
        lines: current.lines
          .filter((_, index) => index !== lineIndex)
          .map((line, index) => ({ ...line, lineIndex: index })),
      };
    });
  };

  const duplicateLine = (lineIndex: number) => {
    setOrder((current) => {
      const target = current.lines[lineIndex];
      if (!target) return current;
      const clone = {
        ...target,
        purchaseLineId: undefined,
        lineIndex: lineIndex + 1,
        collapsed: false,
        skus: target.skus.map((sku) => ({
          ...sku,
          purchaseLineSkuId: undefined,
        })),
      };

      const nextLines = [...current.lines];
      nextLines.splice(lineIndex + 1, 0, clone);
      return {
        ...current,
        lines: nextLines.map((line, index) => ({ ...line, lineIndex: index })),
      };
    });
  };

  const handleSaveDraft = async () => {
    if (!order.purchaseOrderId) return;
    const clientErrors = validateForDraft(order);
    if (clientErrors.length > 0) {
      setValidationErrors(clientErrors);
      return;
    }
    setSaving(true);
    try {
      const saved = await updatePurchaseOrder(order.purchaseOrderId, {
        supplierId: order.supplierId,
        currency: order.currency,
        season: order.season,
        defaultDeliveryMonth: order.defaultDeliveryMonth,
        paymentTermsDays: order.paymentTermsDays ?? 0,
        notes: order.notes,
        supplierOrderNumber: order.supplierOrderNumber,
        lines: order.lines,
      });
      setOrder(saved);
      setSavedFingerprint(orderFingerprint(saved));
      setToastMessage(
        order.status === 'draft' ? 'Utkast sparat' : 'Ändringar sparade',
      );
      setAutoSavedAt(new Date());
    } catch (error) {
      console.error('Save failed:', error);
      setErrorMessage(
        order.status === 'draft'
          ? 'Kunde inte spara utkastet.'
          : 'Kunde inte spara ändringarna.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!order.purchaseOrderId) return;
    const clientErrors = validateForSubmit(order);
    if (clientErrors.length > 0) {
      setValidationErrors(clientErrors);
      return;
    }
    setSaving(true);
    try {
      const submitted = await submitPurchaseOrder(order.purchaseOrderId);
      setOrder(submitted);
      setSavedFingerprint(orderFingerprint(submitted));
      setToastMessage('Inköpsorder skapad');
      navigate('/purchasing/purchase-orders');
    } catch (error: any) {
      const details = error?.response?.data?.details;
      setErrorMessage(
        Array.isArray(details) && details.length > 0
          ? `Ordern är inte redo: ${details.join(', ')}`
          : 'Kunde inte skapa inköpsordern.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleTransferToViskan = async () => {
    if (!order.purchaseOrderId) return;
    if (!order.readyForTransfer) return;
    setSaving(true);
    setErrorMessage(null);
    setValidationErrors([]);
    try {
      const transferResult = await transferPurchaseOrder(order.purchaseOrderId);
      setErrorMessage(null);
      setToastMessage(
        transferResult.results.length > 0
          ? `${transferResult.results.length} produkter skapade i Viskan`
          : 'Överföringen till Viskan är klar',
      );
      const refreshed = await fetchPurchaseOrderById(order.purchaseOrderId);
      setOrder(refreshed);
      setSavedFingerprint(orderFingerprint(refreshed));
    } catch (error: any) {
      const data = error?.response?.data;
      const failedProducts = (
        Array.isArray(data?.failedProducts) ? data.failedProducts : []
      ) as Array<{ productNumber?: string; error?: string }>;
      const failedSummary =
        failedProducts.length > 0
          ? ` ${failedProducts
              .map(
                (p) =>
                  `${p.productNumber || 'Produkt'}: ${p.error || 'Okänt fel'}`,
              )
              .join(', ')}`
          : '';
      setErrorMessage(
        `${data?.error || 'Kunde inte skapa produkter i Viskan.'}${failedSummary}`,
      );
      try {
        const refreshed = await fetchPurchaseOrderById(order.purchaseOrderId);
        setOrder(refreshed);
        setSavedFingerprint(orderFingerprint(refreshed));
      } catch {
        // Keep the transfer error visible even if the follow-up refresh fails.
      }
    } finally {
      setSaving(false);
    }
  };

  const handleForceTransferToViskan = async () => {
    if (!order.purchaseOrderId) return;
    setForceTransferConfirmOpen(false);
    setSaving(true);
    setErrorMessage(null);
    setValidationErrors([]);
    try {
      const transferResult = await transferPurchaseOrder(
        order.purchaseOrderId,
        {
          forceMissingSize: true,
        },
      );
      setToastMessage(
        transferResult.results.length > 0
          ? `${transferResult.results.length} produkter skapade i Viskan (saknad storlek satt till ${FORCE_TRANSFER_MISSING_SIZE_CODE})`
          : `Överföringen till Viskan är klar (saknad storlek satt till ${FORCE_TRANSFER_MISSING_SIZE_CODE})`,
      );
      const refreshed = await fetchPurchaseOrderById(order.purchaseOrderId);
      setOrder(refreshed);
      setSavedFingerprint(orderFingerprint(refreshed));
    } catch (error: any) {
      const data = error?.response?.data;
      const failedProducts = (
        Array.isArray(data?.failedProducts) ? data.failedProducts : []
      ) as Array<{ productNumber?: string; error?: string }>;
      const failedSummary =
        failedProducts.length > 0
          ? ` ${failedProducts
              .map(
                (p) =>
                  `${p.productNumber || 'Produkt'}: ${p.error || 'Okänt fel'}`,
              )
              .join(', ')}`
          : '';
      setErrorMessage(
        `${data?.error || 'Kunde inte tvinga överföring till Viskan.'}${failedSummary}`,
      );
      try {
        const refreshed = await fetchPurchaseOrderById(order.purchaseOrderId);
        setOrder(refreshed);
        setSavedFingerprint(orderFingerprint(refreshed));
      } catch {
        // Keep the transfer error visible even if the follow-up refresh fails.
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAttribute1Created = async (attribute: AttributeOption) => {
    setToastMessage(`Färgen ${attribute.code} är upplagd`);
    try {
      const attributeData = await fetchAttributes();
      setAttributes(attributeData);

      if (order.purchaseOrderId && order.status !== 'draft' && !isDirty) {
        const refreshed = await fetchPurchaseOrderById(order.purchaseOrderId);
        const nextOrder = {
          ...refreshed,
          lines: refreshed.lines.map((line) => ({
            ...line,
            collapsed: false,
          })),
        };
        setOrder(nextOrder);
        setSavedFingerprint(orderFingerprint(nextOrder));
      }
    } catch (error) {
      console.error('Failed to refresh after color creation:', error);
      setAttributes((current) => ({
        ...current,
        attribute1: Array.from(
          new Map(
            [...current.attribute1, attribute].map((option) => [
              option.code,
              option,
            ]),
          ).values(),
        ).sort((a, b) => a.code.localeCompare(b.code, 'sv')),
      }));
    }
  };

  const handleExportNotePdf = async () => {
    if (!order.purchaseOrderId) return;
    setSaving(true);
    try {
      const blob = await downloadPurchaseOrderNotePdf({
        purchaseOrderId: order.purchaseOrderId,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const orderNo = (order.orderNumber || String(order.purchaseOrderId))
        .replace(/[^a-z0-9._-]+/gi, '_')
        .slice(0, 80);
      a.href = url;
      a.download = `inkopsnota-${orderNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export purchase order note PDF:', error);
      setErrorMessage('Kunde inte exportera inköpsnotan som PDF.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const keyboardListener = (event: KeyboardEvent) => {
      if (!editable) return;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        addLine();
      }
    };
    window.addEventListener('keydown', keyboardListener);
    return () => window.removeEventListener('keydown', keyboardListener);
  }, [editable, order.lines.length, order.defaultDeliveryMonth]);

  const autoSaveLabel = useMemo(() => {
    if (!autoSavedAt) return 'Ej sparad';
    return `Auto-sparad ${autoSavedAt.toLocaleTimeString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }, [autoSavedAt]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Dialog
        open={newDialogOpen && !isReadOnly}
        onClose={() => {
          navigate('/purchasing/purchase-orders');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ny inköpsorder</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Välj grunduppgifter för ordern. Du kan alltid ändra dem senare.
            </Alert>

            <SupplierPicker
              suppliers={suppliers}
              value={newForm.supplierId}
              onChange={(supplierId) =>
                setNewForm((current) => ({
                  ...current,
                  supplierId,
                }))
              }
              disabled={saving}
            />

            <SeasonPicker
              value={newForm.season || ''}
              onChange={(code) =>
                setNewForm((current) => ({
                  ...current,
                  season: code || null,
                }))
              }
              disabled={saving}
            />

            <TextField
              fullWidth
              size="small"
              label="Betalningsvillkor (dagar)"
              type="number"
              value={newForm.paymentTermsDays}
              inputProps={{ min: 0, max: 3650, step: 1 }}
              disabled={saving}
              onChange={(event) => {
                const raw = Number(event.target.value);
                const next = Number.isFinite(raw)
                  ? Math.max(0, Math.min(3650, Math.floor(raw)))
                  : 0;
                setNewForm((current) => ({
                  ...current,
                  paymentTermsDays: next,
                }));
              }}
            />

            <TextField
              fullWidth
              size="small"
              label="Anteckning (valfritt)"
              value={newForm.notes}
              disabled={saving}
              multiline
              maxRows={3}
              onChange={(event) =>
                setNewForm((current) => ({
                  ...current,
                  notes: event.target.value,
                }))
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => navigate('/purchasing/purchase-orders')}
            disabled={saving}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            onClick={tryCreateNewOrder}
            disabled={!isNewFormValid || saving}
          >
            Skapa order
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dupConfirmOpen}
        onClose={() => {
          setDupConfirmOpen(false);
          setDupCandidate(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Det finns redan en tom order</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="warning">
              Det finns redan en tom inköpsorder för vald leverantör och säsong.
              Vill du skapa en ny ändå?
            </Alert>
            {dupCandidate && (
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2">
                  Befintlig order: {dupCandidate.orderNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dupCandidate.supplierName || '—'}
                  {dupCandidate.season
                    ? ` • Säsong ${dupCandidate.season}`
                    : ''}
                </Typography>
              </Paper>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (dupCandidate) {
                setDupConfirmOpen(false);
                setDupCandidate(null);
                navigate(
                  `/purchasing/purchase-orders/edit/${dupCandidate.purchaseOrderId}`,
                );
              } else {
                setDupConfirmOpen(false);
              }
            }}
          >
            Öppna befintlig
          </Button>
          <Button
            onClick={() => {
              setDupConfirmOpen(false);
              setDupCandidate(null);
            }}
          >
            Avbryt
          </Button>
          <Button variant="contained" onClick={createNewOrderNow}>
            Skapa ny ändå
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={forceTransferConfirmOpen}
        onClose={() => setForceTransferConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Tvinga överföring?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Aktiva SKU:er utan storlek får{' '}
            <strong>{FORCE_TRANSFER_MISSING_SIZE_CODE}</strong> innan ordern
            överförs till Viskan. Övrig validering (EAN, pris, vikt m.m.) gäller
            fortfarande.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceTransferConfirmOpen(false)}>
            Avbryt
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={saving}
            onClick={handleForceTransferToViskan}
          >
            Tvinga överföring
          </Button>
        </DialogActions>
      </Dialog>

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Tillbaka
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4">
            {isReadOnly
              ? `Inköpsorder ${order.orderNumber || ''}`
              : order.orderNumber
                ? `Redigerar ${order.orderNumber}`
                : 'Ny inköpsorder'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {autoSaveLabel}
          </Typography>
        </Box>
        <Link
          component="button"
          variant="caption"
          onClick={() => addLine()}
          disabled={!editable}
          sx={{ textDecoration: 'none' }}
        >
          ⌘K Ny produkt
        </Link>
        {order.purchaseOrderId && (
          <Button
            variant="outlined"
            disabled={saving}
            onClick={handleExportNotePdf}
          >
            Exportera inköpsnota (PDF)
          </Button>
        )}
        {(order.status === 'submitted' ||
          order.status === 'transfer_failed') && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip
              title={
                order.readyForTransfer
                  ? order.status === 'transfer_failed'
                    ? 'Försök skapa produkterna i Viskan igen'
                    : 'Skapa produkter i Viskan'
                  : (order.missingFieldsSummary || []).length > 0
                    ? `Saknar data: ${formatMissingFields(order.missingFieldsSummary)}`
                    : 'Ordern är inte redo att överföras än'
              }
            >
              <span>
                <Button
                  variant="outlined"
                  disabled={!order.readyForTransfer || saving || isDirty}
                  onClick={handleTransferToViskan}
                >
                  {order.status === 'transfer_failed'
                    ? 'Försök igen i Viskan'
                    : 'Skapa produkter i Viskan'}
                </Button>
              </span>
            </Tooltip>
            {canForceMissingSizeTransfer && (
              <Tooltip
                title={`Sätter storlek ${FORCE_TRANSFER_MISSING_SIZE_CODE} på SKU:er utan storlek och överför`}
              >
                <Link
                  component="button"
                  variant="caption"
                  color="text.secondary"
                  disabled={saving || isDirty}
                  onClick={() => setForceTransferConfirmOpen(true)}
                  sx={{
                    textDecoration: 'none',
                    fontSize: 11,
                    whiteSpace: 'nowrap',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Tvinga överföring
                </Link>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>

      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8.5}>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2}>
              {/* Leverantör — full width on top */}
              <Grid item xs={12} md={5}>
                <SupplierPicker
                  suppliers={suppliers}
                  value={order.supplierId > 0 ? order.supplierId : null}
                  onChange={handleSupplierChange}
                  disabled={!editable}
                />
              </Grid>

              {/* Ordernummer — read-only, auto-generated */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Ordernummer"
                  value={order.orderNumber || '—'}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '0.875rem' },
                  }}
                  helperText="Genereras automatiskt"
                />
              </Grid>

              {/* Leverantörens ordernummer */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Leverantörens ordernr"
                  value={order.supplierOrderNumber ?? ''}
                  disabled={!editable}
                  placeholder="Valfritt"
                  onChange={(event) =>
                    setOrder((current) => ({
                      ...current,
                      supplierOrderNumber: event.target.value || null,
                    }))
                  }
                />
              </Grid>

              {/* Valuta */}
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valuta"
                  select
                  value={order.currency}
                  disabled={!editable}
                  onChange={(event) =>
                    setOrder((current) => ({
                      ...current,
                      currency: event.target.value,
                    }))
                  }
                >
                  {CURRENCIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Säsong */}
              <Grid item xs={6} md={2}>
                <SeasonPicker
                  value={order.season}
                  onChange={(code) => {
                    setOrder((current) => {
                      const newMonths = generateDeliveryMonths(code, null);
                      const stillValid = newMonths.some(
                        (m) => m.value === current.defaultDeliveryMonth,
                      );
                      const nextDefault = stillValid
                        ? current.defaultDeliveryMonth
                        : (newMonths[0]?.value ?? current.defaultDeliveryMonth);
                      return {
                        ...current,
                        season: code,
                        defaultDeliveryMonth: nextDefault,
                        lines: current.lines.map((line) =>
                          line.deliveryMonth === current.defaultDeliveryMonth
                            ? { ...line, deliveryMonth: nextDefault }
                            : line,
                        ),
                      };
                    });
                  }}
                  disabled={!editable}
                />
              </Grid>

              {/* Standard leveransmånad */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Standard leveransmånad"
                  select
                  value={order.defaultDeliveryMonth}
                  disabled={!editable}
                  onChange={(event) => {
                    const next = event.target.value;
                    setOrder((current) => ({
                      ...current,
                      defaultDeliveryMonth: next,
                      lines: current.lines.map((line) =>
                        line.deliveryMonth === current.defaultDeliveryMonth
                          ? { ...line, deliveryMonth: next }
                          : line,
                      ),
                    }));
                  }}
                >
                  {deliveryMonths.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Betalningsvillkor */}
              <Grid item xs={6} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Betalningsvillkor (dagar)"
                  type="number"
                  value={order.paymentTermsDays ?? 0}
                  disabled={!editable}
                  inputProps={{ min: 0, max: 3650, step: 1 }}
                  onChange={(event) => {
                    const raw = Number(event.target.value);
                    const next = Number.isFinite(raw)
                      ? Math.max(0, Math.min(3650, Math.floor(raw)))
                      : 0;
                    setOrder((current) => ({
                      ...current,
                      paymentTermsDays: next,
                    }));
                  }}
                />
              </Grid>

              {/* Anteckningar */}
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Anteckningar"
                  value={order.notes || ''}
                  disabled={!editable}
                  multiline
                  maxRows={2}
                  onChange={(event) =>
                    setOrder((current) => ({
                      ...current,
                      notes: event.target.value,
                    }))
                  }
                />
              </Grid>
            </Grid>
          </Paper>

          <Stack spacing={2}>
            {order.lines.map((line, index) => (
              <PurchaseLine
                key={`${line.purchaseLineId || 'line'}-${index}`}
                line={line}
                index={index}
                supplierId={order.supplierId > 0 ? order.supplierId : null}
                brands={brands}
                internalProducts={[]}
                attribute1Options={attributes.attribute1}
                attribute2Options={attributes.attribute2}
                attribute3Options={attributes.attribute3}
                currency={order.currency}
                deliveryMonths={deliveryMonths}
                readOnly={!editable}
                onAttribute1Created={handleAttribute1Created}
                onChange={(nextLine) => updateLine(index, nextLine)}
                onRemove={() => removeLine(index)}
                onDuplicate={() => duplicateLine(index)}
              />
            ))}
          </Stack>

          {editable && (
            <Button
              sx={{ mt: 2 }}
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addLine}
              fullWidth
            >
              Ny produkt
            </Button>
          )}
        </Grid>

        <Grid item xs={12} md={3.5}>
          <PurchaseOrderSummary
            order={order}
            saving={saving}
            readOnly={!editable}
            onSaveDraft={handleSaveDraft}
            onSubmit={handleSubmit}
          />
        </Grid>
      </Grid>

      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={2500}
        onClose={() => setToastMessage(null)}
        message={toastMessage}
      />

      <Dialog
        open={validationErrors.length > 0}
        onClose={() => setValidationErrors([])}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Valideringsfel</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            {validationErrors.map((error) => (
              <Alert key={error} severity="warning">
                {error}
              </Alert>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationErrors([])}>Stäng</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function validateForDraft(order: PurchaseOrder): string[] {
  const errors: string[] = [];
  const usedArticleNumbers = new Set<string>();
  const usedPlu = new Set<string>();
  const usedEan = new Set<string>();

  order.lines.forEach((line, lineIndex) => {
    const articleKey = line.articleNumber?.trim().toLowerCase();
    if (articleKey) {
      if (usedArticleNumbers.has(articleKey)) {
        errors.push(
          `Rad ${lineIndex + 1}: dubbelt artikelnummer (${line.articleNumber?.trim()})`,
        );
      } else {
        usedArticleNumbers.add(articleKey);
      }
    }

    line.skus.forEach((sku, skuIndex) => {
      const prefix = `Rad ${lineIndex + 1}, SKU ${skuIndex + 1}`;
      const plu = sku.pluNo?.trim();
      if (plu) {
        if (usedPlu.has(plu)) {
          errors.push(`${prefix}: dubbelt PLU (${plu})`);
        } else {
          usedPlu.add(plu);
        }
      }

      const ean = sku.ean13?.trim();
      if (ean) {
        if (!/^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(ean)) {
          errors.push(`${prefix}: EAN måste vara 8, 12, 13 eller 14 siffror`);
        } else if (usedEan.has(ean)) {
          errors.push(`${prefix}: dubbelt EAN (${ean})`);
        } else {
          usedEan.add(ean);
        }
      }

      if (Number(sku.quantity) < 0) {
        errors.push(`${prefix}: antal får inte vara negativt`);
      }
    });
  });

  return Array.from(new Set(errors));
}

function validateForSubmit(order: PurchaseOrder): string[] {
  const errors: string[] = [];
  const usedArticleNumbers = new Set<string>();
  const usedPlu = new Set<string>();
  const usedEan = new Set<string>();

  if (!(Number(order.paymentTermsDays) > 0)) {
    errors.push('Betalningsvillkor måste vara högre än 0 dagar');
  }

  order.lines.forEach((line, lineIndex) => {
    if (!line.articleNumber?.trim()) {
      errors.push(`Rad ${lineIndex + 1}: artikelnummer saknas`);
    } else {
      const articleKey = line.articleNumber.trim().toLowerCase();
      if (usedArticleNumbers.has(articleKey)) {
        errors.push(
          `Rad ${lineIndex + 1}: dubbelt artikelnummer (${line.articleNumber.trim()})`,
        );
      } else {
        usedArticleNumbers.add(articleKey);
      }
    }
    if (!line.productName?.trim()) {
      errors.push(`Rad ${lineIndex + 1}: artikelnamn saknas`);
    }
    const activeSkus = line.skus.filter((sku) => Number(sku.quantity) > 0);
    if (
      activeSkus.length > 0 &&
      activeSkus.some((sku) => !(Number(sku.weight) > 0))
    ) {
      errors.push(`Rad ${lineIndex + 1}: vikt saknas`);
    }

    line.skus.forEach((sku, skuIndex) => {
      const prefix = `Rad ${lineIndex + 1}, SKU ${skuIndex + 1}`;
      const plu = sku.pluNo?.trim();
      // PLU is handled server-side (auto-generated) when needed,
      // but we still guard against duplicates if a user filled it in.
      if (plu) {
        if (usedPlu.has(plu)) {
          errors.push(`${prefix}: dubbelt PLU (${plu})`);
        } else {
          usedPlu.add(plu);
        }
      }

      const ean = sku.ean13?.trim();
      if (ean) {
        if (!/^(?:\d{8}|\d{12}|\d{13}|\d{14})$/.test(ean)) {
          errors.push(`${prefix}: EAN måste vara 8, 12, 13 eller 14 siffror`);
        } else if (usedEan.has(ean)) {
          errors.push(`${prefix}: dubbelt EAN (${ean})`);
        } else {
          usedEan.add(ean);
        }
      }

      if (Number(sku.quantity) < 0) {
        errors.push(`${prefix}: antal får inte vara negativt`);
      }
    });
  });

  return Array.from(new Set(errors));
}

export default PurchaseOrderEdit;
